import React from 'react'

// Vendor
import { useTranslation } from 'react-i18next'

// Reactor UI
import { Flex, Box } from 'reactor-ui'
import Button from 'reactor-ui/components/Button'
import Alert from 'reactor-ui/components/Alert'
import Icon from 'reactor-ui/components/Icon'
import Card from 'reactor-ui/components/Card'

import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure
} from "reactor-ui/components/Modal"

import {
  MenuItem
} from 'reactor-ui/components/Menu'


// Reactor
import { Form, RfFormError, RfFormField, useReactorActionForm, useFormState, RfFormFields } from 'reactor-form/components/RfForm'
import { isFunction } from 'lodash'


const RfActionForm = ({
  name,
  actionKey,
  header,
  initialValues,
  displayAs = 'MODAL',
  onSuccess,
  onError,
  buttonText = 'btn.save',
  buttonColorScheme = 'brand',
  fields,
  anchorKind,
  anchor,
  anchorProps,
  onClose,
  displayProps,
  elements,
  icon,
  sx,
  isResetEnabled = true,
  fieldElements,
  closeOnSuccess,
  formComponent,
  displayComponent,
  size = 'lg',
  headerText,
  footerText,
  ctx,
  showSuccess = true,
  anchorIconOnly,
  ...rest
}) => {
  const { t } = useTranslation()
  const hasUpload = React.useMemo(() => {
    let hasUpload = false
    fields?.forEach(field => {
      if (field.kind === 'file') hasUpload = true
    })
    return hasUpload
  }, [])
  const disclosure = useDisclosure()
  const [formProps, isSubmitting, reset] = useReactorActionForm(
    actionKey,
    {
      hasUpload,
      initialValues,
      onSuccess: (data) => {
        onSuccess?.(data)
        closeOnSuccess && disclosure.onClose()
      },
      onError
    }
  )

  let content
  let footerButtons = []

  if (formProps.phase === 'SUCCESS' && showSuccess) {
    isResetEnabled && footerButtons.push(
      <Button key='reset' variant='ghost' colorScheme='brand' onClick={reset} sx={{
        textTransform: 'capitalize'
      }}>
        {t('btn.refill_form_again')}
      </Button>
    )
    content = elements?.success ? (
      isFunction(elements.success) ? elements.success(formProps.result) : elements.success
    ) : (
      <Box>
        <Alert status='success' sx={{ textTransform: 'capitalize' }}>
          {elements?.successMessage ?
            isFunction(elements.successMessage) ? elements?.successMessage?.(formProps.result) : elements.successMessage
            :
            t(formProps.result?.message || 'text.SUCCESS')
          }
        </Alert>
      </Box>
    )
  } else {
    if (displayComponent) {
      content = displayComponent
    } else {
      content = (
        <Form {...formProps} ctx={ctx}>
          <RfFormFields fields={fields} fieldSx={{
            mb: 4
          }} />
          {formComponent}
          {fieldElements}
          <RfFormError error={formProps.error} />
          <Button mt={3} textTransform='capitalize' colorScheme={buttonColorScheme} tabIndex={4} submit isLoading={isSubmitting} isFullWidth>
            {t(buttonText)}
          </Button>
        </Form>
      )
    }
  }

  if (!displayAs || displayAs === 'MODAL') {
    return (
      <ModalAction anchorProps={anchorProps} size={size} disclosure={disclosure} icon={icon} sx={sx} onClose={() => {
        console.log('reset')
        reset()
        onClose?.()
      }} anchor={anchor} anchorKind={anchorKind} anchorIconOnly={anchorIconOnly} header={header || t(name)} footerButtons={footerButtons}>
        {headerText && (
          <Box my={2} color='dark.400'>
            {headerText}
          </Box>
        )}
        {content}
      </ModalAction>
    )
  } else if (displayAs === 'MENU') {
    return (
      <ModalAction anchorProps={anchorProps} size={size} disclosure={disclosure} icon={icon} sx={sx} onClose={() => {
        reset()
        onClose?.()
      }} anchor={anchor} anchorKind={anchorKind} anchorIconOnly={anchorIconOnly} header={header || t(name)} footerButtons={footerButtons}>
        {headerText && (
          <Box my={2} color='dark.400'>
            {headerText}
          </Box>
        )}
        {content}
      </ModalAction>
    )
  } else if (displayAs === 'FORM') {
    return (
      <Box sx={sx}>
        <Card.Header sx={{ textTransform: 'capitalize', mb: 2 }}>
          {name ? t(name) : <Icon name={icon} />}
        </Card.Header>

        {headerText && (
          <Box my={2} color='dark.400'>
            {headerText}
          </Box>
        )}
        {content}
        {onClose && <Button sx={{ textTransform: 'capitalize' }} variant='ghost' onClick={onClose}>
          {t('op.close')}
        </Button>}
      </Box>
    )
  } else {
    return `${displayAs}`
  }

  return null
}

const ModalAction = ({
  disclosure,
  children,
  header,
  footerButtons,
  onClose: onCloseFromProps,
  anchorKind = 'BUTTON',
  anchorProps,
  anchor: anchorFromProps,
  icon,
  size = 'lg',
  sx,
  anchorIconOnly
}) => {
  const { t } = useTranslation()
  const { isOpen, onOpen, onClose } = disclosure

  let anchor
  if (anchorFromProps) {
    anchor = React.cloneElement(anchorFromProps, { onClick: onOpen })
  } else {
    const anchorContent = icon ? (
      (!anchorIconOnly && header) ? <Icon name={icon}>{header}</Icon> : <Icon {...anchorProps} name={icon} />
    ) : header
    if (anchorKind === 'BUTTON') {
      anchor = (
        <Button
          {...anchorProps}
          sx={{
            ...anchorProps?.sx,
            textTransform: 'capitalize'
          }}
          size='sm'
          textTransform='capitalize'
          colorScheme={'brand'}
          borderRadius={3}
          onClick={onOpen}>
          {anchorContent}
        </Button>
      )
    } else if (anchorKind === 'LINK') {
      anchor = <Button {...anchorProps} sx={{
        ...anchorProps?.sx,
        textTransform: 'capitalize'
      }} variant='link' textTransform='capitalize' colorScheme={'brand'} onClick={onOpen}>{anchorContent}</Button>
    } else if (anchorKind === 'MENUITEM') {
      anchor = (
        <MenuItem onClick={onOpen} sx={{
          textTransform: 'capitalize'
        }}>{anchorContent}</MenuItem>
      )
    }
  }
  return (
    <Box sx={sx}>
      {anchor}

      <Modal
        autoFocus={false}
        closeOnOverlayClick={false}
        isOpen={isOpen}
        onClose={() => {
          onCloseFromProps?.()
          onClose()
        }}
        size={size}
        trapFocus={false}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader textTransform='capitalize'>{header}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {children}
          </ModalBody>

          <ModalFooter>
            {footerButtons}
            <Button sx={{ textTransform: 'capitalize' }} variant='ghost' onClick={() => {
              onCloseFromProps?.()
              onClose()
            }}>
              {t('op.close')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  )
}

export default RfActionForm